import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users,
  TrendingUp,
  CreditCard,
  DollarSign,
  Activity,
  BarChart3,
  AlertTriangle,
  ChevronRight,
  Search,
  Download,
  RefreshCw,
  Lightbulb,
  Target,
  Wallet,
  Calendar,
  Eye,
  Mail,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  Crown
} from "lucide-react";

// Recharts for visualizations
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  FunnelChart,
  Funnel,
  LabelList
} from "recharts";

const FUNNEL_COLORS = ['#0F5F5C', '#14807C', '#2DD4BF', '#34D399', '#4ADE80', '#86EFAC'];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [insights, setInsights] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [users, setUsers] = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [emailStats, setEmailStats] = useState(null);
  const [conversion, setConversion] = useState(null);
  
  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [userPlanFilter, setUserPlanFilter] = useState('all');
  const [userPage, setUserPage] = useState(1);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/calendar');
    }
  }, [user, navigate]);

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        funnelRes,
        featureRes,
        insightsRes,
        activityRes,
        growthRes,
        emailRes,
        conversionRes
      ] = await Promise.all([
        axios.get(`${API}/admin/stats/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/funnel`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/feature-usage`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/insights`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/activity-feed?limit=20`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/user-growth?days=30`, { withCredentials: true }),
        axios.get(`${API}/admin/email-stats`, { withCredentials: true }),
        axios.get(`${API}/admin/stats/conversion`, { withCredentials: true })
      ]);

      setOverview(overviewRes.data);
      setFunnel(funnelRes.data);
      setFeatureUsage(featureRes.data);
      setInsights(insightsRes.data);
      setActivityFeed(activityRes.data.events || []);
      setUserGrowth(growthRes.data.growth || []);
      setEmailStats(emailRes.data);
      setConversion(conversionRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { page: userPage, limit: 20, search: userSearch, plan_filter: userPlanFilter },
        withCredentials: true
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (user?.is_admin && activeTab === 'users') {
      fetchUsers();
    }
  }, [user, activeTab, userPage, userSearch, userPlanFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleExportEmails = () => {
    window.open(`${API}/admin/email-list/export?plan_filter=${userPlanFilter}`, '_blank');
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-4">You don&apos;t have admin privileges</p>
          <Button onClick={() => navigate('/calendar')} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-emerald-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'funnel', label: 'Funnel', icon: Target },
    { id: 'features', label: 'Features', icon: Activity },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'insights', label: 'Insights', icon: Lightbulb }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Path2Plenty Admin</h1>
              <p className="text-sm text-slate-400">Back Office Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/calendar')}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Exit Admin
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 bg-slate-800 border-r border-slate-700 min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeTab === tab.id
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && overview && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KPICard
                  title="Total Users"
                  value={overview.total_users}
                  icon={Users}
                  color="emerald"
                />
                <KPICard
                  title="DAU"
                  value={overview.dau}
                  subtitle="Today"
                  icon={Activity}
                  color="blue"
                />
                <KPICard
                  title="WAU"
                  value={overview.wau}
                  subtitle="7 days"
                  icon={Activity}
                  color="purple"
                />
                <KPICard
                  title="Trial Starts"
                  value={overview.trial_starts}
                  icon={Target}
                  color="amber"
                />
                <KPICard
                  title="Conversion"
                  value={`${overview.conversion_rate}%`}
                  subtitle="Trial → Paid"
                  icon={TrendingUp}
                  color="green"
                />
                <KPICard
                  title="MRR"
                  value={`£${overview.mrr.toLocaleString()}`}
                  icon={DollarSign}
                  color="gold"
                />
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* User Growth Chart */}
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-4">User Growth (30 days)</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={userGrowth}>
                        <defs>
                          <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#64748B"
                          tickFormatter={(v) => v.slice(5)}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis stroke="#64748B" tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1E293B', 
                            border: '1px solid #334155',
                            borderRadius: '8px'
                          }}
                          labelStyle={{ color: '#94A3B8' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="cumulative"
                          stroke="#10B981"
                          fillOpacity={1}
                          fill="url(#colorUsers)"
                          name="Total Users"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Real-Time Activity Feed */}
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {activityFeed.slice(0, 10).map((event) => (
                      <div key={event.event_id || `${event.timestamp}-${event.event_name}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-700/50">
                        <ActivityIcon eventName={event.event_name} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{event.message}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(event.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    {activityFeed.length === 0 && (
                      <p className="text-slate-400 text-center py-8">No recent activity</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">New Today</p>
                  <p className="text-3xl font-bold text-emerald-400">{overview.new_signups_today}</p>
                  <p className="text-xs text-slate-500 mt-1">signups</p>
                </div>
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">This Week</p>
                  <p className="text-3xl font-bold text-blue-400">{overview.new_signups_week}</p>
                  <p className="text-xs text-slate-500 mt-1">signups</p>
                </div>
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">Churn Rate</p>
                  <p className="text-3xl font-bold text-red-400">{overview.churn_rate}%</p>
                  <p className="text-xs text-slate-500 mt-1">past 30 days</p>
                </div>
              </div>
            </div>
          )}

          {/* Funnel Tab */}
          {activeTab === 'funnel' && funnel && (
            <div className="space-y-6">
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Conversion Funnel</h3>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Overall Conversion</p>
                    <p className="text-2xl font-bold text-emerald-400">{funnel.overall_conversion}%</p>
                  </div>
                </div>

                {/* Funnel Steps */}
                <div className="space-y-4">
                  {funnel.funnel.map((step, index) => {
                    const width = index === 0 ? 100 : (step.count / funnel.funnel[0].count) * 100;
                    const prevCount = index > 0 ? funnel.funnel[index - 1].count : step.count;
                    const retained = prevCount > 0 ? ((step.count / prevCount) * 100).toFixed(1) : 100;
                    
                    return (
                      <div key={step.stage} className="relative">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-medium text-white">
                              {index + 1}
                            </span>
                            <span className="font-medium text-white">{step.stage}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-2xl font-bold text-white">{step.count.toLocaleString()}</span>
                            {index > 0 && (
                              <span className={`text-sm font-medium ${
                                step.dropoff > 50 ? 'text-red-400' : 
                                step.dropoff > 25 ? 'text-amber-400' : 'text-emerald-400'
                              }`}>
                                {retained}% retained
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="h-10 rounded-lg bg-slate-700 overflow-hidden">
                          <div
                            className="h-full rounded-lg transition-all duration-500"
                            style={{
                              width: `${width}%`,
                              backgroundColor: FUNNEL_COLORS[index % FUNNEL_COLORS.length]
                            }}
                          />
                        </div>
                        {step.dropoff > 0 && (
                          <p className="text-xs text-red-400 mt-1">
                            ↓ {step.dropoff}% drop-off
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Conversion Stats */}
              {conversion && (
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                    <p className="text-slate-400 text-sm mb-1">Trial Start Rate</p>
                    <p className="text-3xl font-bold text-blue-400">{conversion.trial_start_rate}%</p>
                  </div>
                  <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                    <p className="text-slate-400 text-sm mb-1">Trial → Paid</p>
                    <p className="text-3xl font-bold text-emerald-400">{conversion.trial_to_paid_rate}%</p>
                  </div>
                  <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                    <p className="text-slate-400 text-sm mb-1">Avg Days to Convert</p>
                    <p className="text-3xl font-bold text-amber-400">{conversion.avg_days_to_conversion}</p>
                  </div>
                  <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                    <p className="text-slate-400 text-sm mb-1">Total Conversions</p>
                    <p className="text-3xl font-bold text-purple-400">{conversion.total_conversions}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && featureUsage && (
            <div className="space-y-6">
              {/* Activation Metrics */}
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Activation Metrics</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {featureUsage.activation_metrics.map((metric) => (
                    <div key={metric.feature} className="bg-slate-700/50 rounded-xl p-4">
                      <p className="text-slate-400 text-sm mb-1">{metric.feature}</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-2xl font-bold text-white">{metric.percent}%</p>
                        <p className="text-sm text-slate-500">({metric.users} users)</p>
                      </div>
                      <div className="h-2 bg-slate-600 rounded-full mt-3 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${metric.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conversion Drivers */}
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Conversion Drivers</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {featureUsage.conversion_drivers.map((metric) => (
                    <div key={metric.feature} className="bg-slate-700/50 rounded-xl p-4">
                      <p className="text-slate-400 text-sm mb-1">{metric.feature}</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-2xl font-bold text-amber-400">{metric.percent}%</p>
                        <p className="text-sm text-slate-500">({metric.users} users)</p>
                      </div>
                      <div className="h-2 bg-slate-600 rounded-full mt-3 overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full"
                          style={{ width: `${metric.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Most Used Features */}
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Feature Ranking</h3>
                <div className="space-y-3">
                  {featureUsage.most_used.map((feature, index) => (
                    <div key={feature.feature} className="flex items-center gap-4">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-amber-500 text-white' :
                        index === 1 ? 'bg-slate-400 text-white' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-slate-700 text-slate-400'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-white">{feature.feature}</p>
                      </div>
                      <p className="text-lg font-bold text-emerald-400">{feature.users} users</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              {/* Search & Filters */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by email or name..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <Select value={userPlanFilter} onValueChange={setUserPlanFilter}>
                  <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Filter by plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Users Table */}
              <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-700/50">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">User</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Plan</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Activity</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Last Active</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {users.users.map((u) => (
                      <tr key={u.user_id} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-white">{u.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-400">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <PlanBadge plan={u.plan_type} />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <span>{u.income_count} income</span>
                            <span>•</span>
                            <span>{u.bills_count} bills</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-400">
                          {u.last_active ? new Date(u.last_active).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-400">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400">
                    Showing {users.users.length} of {users.total} users
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={userPage === 1}
                      onClick={() => setUserPage(userPage - 1)}
                      className="border-slate-700 text-slate-400"
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-slate-400">
                      Page {users.page} of {users.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={userPage >= users.pages}
                      onClick={() => setUserPage(userPage + 1)}
                      className="border-slate-700 text-slate-400"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Emails Tab */}
          {activeTab === 'emails' && emailStats && (
            <div className="space-y-6">
              {/* Email Stats */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">Total Subscribers</p>
                  <p className="text-3xl font-bold text-emerald-400">{emailStats.total_subscribers}</p>
                </div>
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">New Today</p>
                  <p className="text-3xl font-bold text-blue-400">{emailStats.new_today}</p>
                </div>
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">This Week</p>
                  <p className="text-3xl font-bold text-purple-400">{emailStats.new_this_week}</p>
                </div>
                <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
                  <Button onClick={handleExportEmails} className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* By Plan Type */}
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Subscribers by Plan</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-4xl font-bold text-slate-300">{emailStats.by_plan.free}</p>
                    <p className="text-slate-400 mt-1">Free Users</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-4xl font-bold text-amber-400">{emailStats.by_plan.trial}</p>
                    <p className="text-slate-400 mt-1">Trial Users</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                    <p className="text-4xl font-bold text-emerald-400">{emailStats.by_plan.premium}</p>
                    <p className="text-slate-400 mt-1">Premium Users</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Insights Tab */}
          {activeTab === 'insights' && insights && (
            <div className="space-y-6">
              <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Automated Insights</h3>
                    <p className="text-sm text-slate-400">AI-generated recommendations based on your data</p>
                  </div>
                </div>

                {insights.insights.length > 0 ? (
                  <div className="space-y-4">
                    {insights.insights.map((insight) => (
                      <div 
                        key={`${insight.type}-${insight.title}`}
                        className={`p-5 rounded-xl border ${
                          insight.priority === 'high' 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : insight.priority === 'medium'
                              ? 'bg-amber-500/10 border-amber-500/30'
                              : 'bg-blue-500/10 border-blue-500/30'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <InsightIcon type={insight.type} priority={insight.priority} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-white">{insight.title}</h4>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                insight.priority === 'high' 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : insight.priority === 'medium'
                                    ? 'bg-amber-500/20 text-amber-400'
                                    : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {insight.priority}
                              </span>
                            </div>
                            <p className="text-slate-300 mb-3">{insight.message}</p>
                            <p className="text-sm text-slate-400 flex items-center gap-2">
                              <ChevronRight className="w-4 h-4" />
                              {insight.action}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <p className="text-slate-300 text-lg">All systems healthy!</p>
                    <p className="text-slate-500">No critical insights at this time</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// Helper Components
const KPICard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colors = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/20 text-amber-400',
    green: 'bg-green-500/20 text-green-400',
    gold: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-400">{title}</p>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
  );
};

const PlanBadge = ({ plan }) => {
  const styles = {
    free: 'bg-slate-700 text-slate-300',
    trial: 'bg-amber-500/20 text-amber-400',
    premium: 'bg-emerald-500/20 text-emerald-400'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[plan] || styles.free}`}>
      {plan || 'free'}
    </span>
  );
};

const ActivityIcon = ({ eventName }) => {
  const icons = {
    signup: { icon: Users, color: 'text-emerald-400 bg-emerald-500/20' },
    added_income: { icon: TrendingUp, color: 'text-green-400 bg-green-500/20' },
    added_bill: { icon: CreditCard, color: 'text-red-400 bg-red-500/20' },
    viewed_calendar: { icon: Calendar, color: 'text-blue-400 bg-blue-500/20' },
    viewed_forecast: { icon: BarChart3, color: 'text-purple-400 bg-purple-500/20' },
    used_simulation: { icon: Target, color: 'text-amber-400 bg-amber-500/20' },
    upgraded_to_premium: { icon: Crown, color: 'text-amber-400 bg-amber-500/20' },
    hit_paywall: { icon: Eye, color: 'text-slate-400 bg-slate-500/20' }
  };

  const config = icons[eventName] || { icon: Activity, color: 'text-slate-400 bg-slate-500/20' };
  const Icon = config.icon;

  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.color}`}>
      <Icon className="w-4 h-4" />
    </div>
  );
};

const InsightIcon = ({ type, priority }) => {
  const colors = {
    high: 'bg-red-500/20 text-red-400',
    medium: 'bg-amber-500/20 text-amber-400',
    low: 'bg-blue-500/20 text-blue-400'
  };

  const icons = {
    conversion_driver: TrendingUp,
    drop_off: ArrowDownRight,
    activation: Target,
    feature_adoption: Wallet
  };

  const Icon = icons[type] || Lightbulb;

  return (
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[priority]}`}>
      <Icon className="w-6 h-6" />
    </div>
  );
};
